import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, tokens, type CurrentUser } from './api';
import { resetShiftPrompt } from './shift-prompt';
import { dropDesktopNotificationsLocally, forgetDesktopNotifications, resetDesktopPrompt } from './desktop-notifications';

/**
 * Auth state. Permissions come from `/auth/me`, so the UI hides what the server
 * would refuse anyway — the server check (§36.2) is still the real gate; this
 * just stops the interface offering actions that cannot succeed.
 */

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (...permissions: string[]) => boolean;
  canAny: (...permissions: string[]) => boolean;
  isLeaderOf: (departmentId: string) => boolean;
  ledDepartmentIds: string[];
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  // Whatever one session fetched must never show in the next one on the same
  // browser — an employee's salary least of all, which only some may see. Left
  // in the cache, a page fetched under 20 s ago would be shown as it was.
  const forgetSession = useCallback(() => queryClient.clear(), [queryClient]);

  const loadUser = useCallback(async () => {
    if (!tokens.access) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api.get<CurrentUser>('/auth/me'));
    } catch {
      tokens.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
    // The API client fires this when a refresh fails, so a dead session clears
    // the UI immediately instead of on the next navigation.
    const onSignedOut = () => {
      forgetSession();
      setUser(null);
      // No session, no notifications on the desktop: the next sign-in here brings them back.
      dropDesktopNotificationsLocally();
    };
    window.addEventListener('viganium:signed-out', onSignedOut);
    return () => window.removeEventListener('viganium:signed-out', onSignedOut);
  }, [loadUser, forgetSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    // Named explicitly: the API would assume the admin anyway, but a session
    // that says which system it is for never has to be guessed about.
    const result = await api.post<{ accessToken: string; refreshToken: string; user: CurrentUser }>(
      '/auth/login',
      { email, password, app: 'ADMIN' },
    );
    tokens.set(result.accessToken, result.refreshToken);
    // The session before may have ended without a sign-out — an expired token.
    forgetSession();
    // A new sign-in puts the shift controls in front of the person again — and
    // the offer of desktop notifications, where it was put off.
    resetShiftPrompt();
    resetDesktopPrompt();
    setUser(result.user);
  }, [forgetSession]);

  const signOut = useCallback(async () => {
    // This browser stops showing their notifications — said while the session still can.
    await forgetDesktopNotifications().catch(() => undefined);
    try {
      await api.post('/auth/logout', { refreshToken: tokens.refresh ?? undefined });
    } catch {
      // Signing out locally matters more than the server round-trip succeeding.
    }
    tokens.clear();
    forgetSession();
    setUser(null);
  }, [forgetSession]);

  const can = useCallback(
    (...permissions: string[]) => permissions.every((p) => user?.permissions.includes(p) ?? false),
    [user],
  );

  const canAny = useCallback(
    (...permissions: string[]) => permissions.some((p) => user?.permissions.includes(p) ?? false),
    [user],
  );

  const ledDepartmentIds = useMemo(
    () => user?.departments.filter((d) => d.memberType === 'LEADER').map((d) => d.id) ?? [],
    [user],
  );

  const isLeaderOf = useCallback(
    (departmentId: string) => ledDepartmentIds.includes(departmentId),
    [ledDepartmentIds],
  );

  const value = useMemo(
    () => ({ user, loading, signIn, signOut, refreshUser: loadUser, can, canAny, isLeaderOf, ledDepartmentIds }),
    [user, loading, signIn, signOut, loadUser, can, canAny, isLeaderOf, ledDepartmentIds],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}

/**
 * Mirrors src/common/permissions.ts on the backend — the permissions an admin
 * role can hold. Finance and payroll are sys's; an admin session never carries
 * them, so they are not listed here.
 */
export const P = {
  USERS_READ: 'users.read',
  USERS_MANAGE: 'users.manage',
  ROLES_MANAGE: 'roles.manage',
  /** Sees and works with every employee; anyone else, the people in their own departments. */
  USERS_VIEW_ALL: 'users.view_all',
  /** Deletes an employee for good, with everything that is theirs — the Administrator's alone. */
  USERS_DELETE: 'users.delete',
  DEPARTMENTS_READ: 'departments.read',
  DEPARTMENTS_MANAGE: 'departments.manage',
  PROJECTS_CREATE: 'projects.create',
  /** Edit a project, change its status, take people off it. */
  PROJECTS_MANAGE: 'projects.manage',
  PROJECTS_VIEW_ALL: 'projects.view_all',
  // Adding people to a project — the team leaders' too, who take nobody off (2026-10-11).
  PROJECT_MEMBERS_ADD: 'project_members.add',
  TASKS_CREATE: 'tasks.create',
  TASKS_ASSIGN: 'tasks.assign',
  TASKS_MANAGE_ALL: 'tasks.manage_all',
  TICKETS_CREATE: 'tickets.create',
  TICKETS_HANDLE: 'tickets.handle',
  TICKETS_ASSIGN: 'tickets.assign',
  TICKETS_OVERRIDE_SLA: 'tickets.override_sla',
  TICKETS_VIEW_ALL: 'tickets.view_all',
  Q_TASKS_CREATE: 'q_tasks.create',
  Q_TASKS_EXECUTE: 'q_tasks.execute',
  Q_TASKS_REORDER: 'q_tasks.reorder',
  Q_TASKS_OVERRIDE: 'q_tasks.override',
  Q_TASKS_VIEW_ALL: 'q_tasks.view_all',
  ANNOUNCEMENTS_MANAGE: 'announcements.manage',
  ANNOUNCEMENTS_ANY_AUDIENCE: 'announcements.any_audience',
  SHIFTS_MANAGE: 'shifts.manage',
  WORK_LOGS_MANAGE: 'work_logs.manage',
  PENALTIES_MANAGE: 'penalties.manage',
  ATTENDANCE_MANAGE: 'attendance.manage',
  ATTENDANCE_LEADER_BREAKS: 'attendance.leader_breaks',
  /** Take the break types kept for administrators (Administrator). */
  ATTENDANCE_ADMIN_BREAKS: 'attendance.admin_breaks',
  /** Approve or reject requests for the break types that need an administrator's approval (Administrator). */
  ATTENDANCE_BREAKS_APPROVE: 'attendance.breaks.approve',
  /** Pause work at any time (Administrator); Settings › Attendance picks the employees who may too. */
  ATTENDANCE_PAUSE: 'attendance.pause',
  /** Disable, enable and remove the computers people run the Desktop Agent on (Administrator). */
  ATTENDANCE_DEVICES_MANAGE: 'attendance.devices.manage',
  LEAVES_APPROVE: 'leaves.approve',
  /** Approve or reject the overtime leaders send, and send it for anyone — approved at once (Administrator). */
  OVERTIME_APPROVE: 'overtime.approve',
  /** Remote work's second approval, once the team leader has given the first (Administrator). */
  REMOTE_WORK_APPROVE: 'remote_work.approve',
  /** See every meeting on the meeting calendar and cancel any (Administrator). Everyone else, theirs; a leader, their people's too. */
  MEETINGS_MANAGE: 'meetings.manage',
  /** Grant a quarter- or half-day reward, paid with the salary, and withdraw one (Administrator; CR-004). */
  REWARDS_MANAGE: 'rewards.manage',
  // Clients — managed in both systems; their money is sys's.
  CLIENTS_VIEW: 'clients.view',
  CLIENTS_CREATE: 'clients.create',
  CLIENTS_UPDATE: 'clients.update',
  CLIENTS_DEACTIVATE: 'clients.deactivate',
  CLIENT_CONTACTS_MANAGE: 'client_contacts.manage',
  // Removing a contact — not the team leaders' (2026-10-11).
  CLIENT_CONTACTS_REMOVE: 'client_contacts.remove',
  // The client portal (clients.viganium.net).
  CLIENT_PORTAL_MANAGE: 'client_portal.manage',
  CLIENT_CRS_MANAGE: 'client_crs.manage',
  /** Every client's complaint about our people, never one about oneself (Administrator). Leaders see those sent to them. */
  CLIENT_COMPLAINTS_MANAGE: 'client_complaints.manage',
  AUDIT_VIEW: 'audit.view',
  SETTINGS_MANAGE: 'settings.manage',
  REPORTS_VIEW: 'reports.view',
  /** The employee mobile app's activity log and phones (Administrator). */
  MOBILE_ACTIVITY_VIEW: 'mobile.activity.view',
  /** The Monitoring section: servers, their URLs, incidents and alerts (Administrator). */
  MONITORING_MANAGE: 'monitoring.manage',
  /** The chat's sticker packs (Administrator). Everyone sends them. */
  CHAT_STICKERS_MANAGE: 'chat.stickers.manage',
  /** Settings › Wallet & Points: what each reward costs in points (Administrator). */
  WALLET_SETTINGS_MANAGE: 'wallet.settings.manage',
  /** Add points to an employee's wallet, with a reason only administrators read (Administrator; CR-004). */
  WALLET_POINTS_GRANT: 'wallet.points.grant',
  /** Calls: every call, and every recording played — never joining a call unseen (Administrator; 2026-10-29). */
  CALLS_VIEW_ALL: 'calls.view_all',
  /** Calls: every call a client made from the portal — the Client calls tab (Administrator; 2026-11-01). */
  CALLS_VIEW_CLIENT_CALLS: 'calls.view_client_calls',
  /** Calls: every call between employees — the Employee calls tab (Administrator; 2026-11-01). */
  CALLS_VIEW_EMPLOYEE_CALLS: 'calls.view_employee_calls',
  /**
   * Viganium Drive: a public link, and an email to a client's contacts — the Administrator's, and the
   * Share Drive outside the company add-on's (2026-11-02). Everyone keeps a drive and shares it inside.
   */
  DRIVE_SHARE_EXTERNAL: 'drive.share_external',
  /** Viganium Drive: every employee's drive, opened and shared — the Administrator's (2026-11-02). */
  DRIVE_VIEW_ALL: 'drive.view_all',
  /**
   * Sites: every site, and Settings › Sites — who else may publish, by name or by department — the
   * Administrator's (2026-11-03). Those picked there look after the sites they make (`sitePublisher`).
   */
  SITES_MANAGE: 'sites.manage',
} as const;
